import { Box, CircularProgress, useTheme } from '@mui/material';
import { UPDropdownHelperText } from 'components/dropdown/styles';
import { UPInputLabel, UPInputTextField } from 'components/input/styles';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENTS__PROCESSING, DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED } from 'translations/constants';
import { isImageFile, isRawImageFormat, resizeImage } from 'utils/fileValidation';
import { ReactComponent as AttachmentIcon } from '../../assets/icons/Attachment.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/Close.svg';
import {
  UIFileInputAttachmentIconContainer,
  UIFileInputClearIconContainer,
  UIFileInputSpinner,
  UPFileInputFieldWrapper,
  UPFileInputStyled,
} from './styles';
import { UPFileInputProps } from './types';

const UPFileInput = (props: UPFileInputProps): JSX.Element => {
  const {
    value,
    onChange,
    label,
    inputName,
    placeholder,
    className,
    error,
    disabled,
    required,
    helperText,
    clearable,
    onValidationError,
    accept,
    resizeImages,
    testId,
  } = props;
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError(undefined);

    const file = e.target.files?.[0];
    if (!file) return;

    if (isRawImageFormat(file.name)) {
      setValidationError(t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED));
      onValidationError?.(t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED));
      onChange(e);
      return;
    }

    if (resizeImages !== false && isImageFile(file)) {
      setIsLoading(true);
      try {
        const resizedFile = await resizeImage(file);
        // Create a new event with the resized file
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(resizedFile);
        const newEvent = { ...e, target: { ...e.target, files: dataTransfer.files } };
        onChange(newEvent);
      } catch (resizeError) {
        // Fall back to original file if resize fails
        onChange(e);
      } finally {
        setIsLoading(false);
      }
    } else {
      onChange(e);
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (disabled) return;

    setValidationError(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange({ target: { files: [] } });
  };

  const displayError = error || !!validationError;
  const displayHelperText = validationError || helperText;

  return (
    <Box className={className}>
      <UPInputLabel>
        {label} {required && '*'}
      </UPInputLabel>
      <UPFileInputFieldWrapper>
        <UPInputTextField
          value={isLoading ? t(DOCUMENTS__PROCESSING) : value}
          placeholder={placeholder}
          error={displayError}
          disabled={disabled}
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: isLoading ? (
              <UIFileInputSpinner>
                <CircularProgress size={16} />
              </UIFileInputSpinner>
            ) : undefined,
            endAdornment: (
              <>
                {!!value && clearable && !isLoading && (
                  <UIFileInputClearIconContainer
                    onClick={handleClear}
                    data-testid={testId ? `${testId}-clear` : undefined}
                  >
                    <CloseIcon fill={theme.palette.primary.main} />
                  </UIFileInputClearIconContainer>
                )}
                <UIFileInputAttachmentIconContainer>
                  <AttachmentIcon />
                </UIFileInputAttachmentIconContainer>
              </>
            ),
            inputProps: { tabIndex: -1, 'data-testid': testId ? `${testId}-field` : undefined },
          }}
        />
        <UPFileInputStyled
          ref={fileInputRef}
          onChange={handleFileChange}
          type="file"
          accept={accept}
          name={inputName}
          disabled={disabled || isLoading}
          required={required}
          data-testid={testId}
        />
      </UPFileInputFieldWrapper>
      {displayError && <UPDropdownHelperText>{displayHelperText}</UPDropdownHelperText>}
    </Box>
  );
};

UPFileInput.defaultProps = {
  className: '',
  error: false,
  required: false,
  helperText: '',
  accept:
    'image/jpeg,image/png,image/gif,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

export default UPFileInput;
